'use client';

import type { PublicUserDocument } from '@models/user';
import ProductPriceRange from '@ui/ProductPriceRange';
import { getItem, logEvent } from '@util/analytics';
import { getCartItemFromProductDocument, cn } from '@util/index';
import { getProductSlug } from '@util/urlHelpers';
import { useAttributionContext } from 'context/AttributionContext';
import { useAuth } from 'context/AuthContext';
import { useSearch } from 'context/SearchContext';
import QuickViewModal from 'features/shop/components/QuickViewModal';
import type { OrderItemDocument } from 'models/order';
import type { ProductDocument } from 'models/product';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useState } from 'react';
import { useAnimation } from 'framer-motion';
import { useShoppingCart } from 'context/ShoppingCartContext';
import useLogWhenInView from '@util/hooks/useLogWhenInView';
import { MotionDiv } from 'motion';
import {
  trackUserInteraction,
  trackUserSearch,
} from '@util/firestore/recommendations';
import { FavoriteButton } from './FavoriteButton';
import { QuickViewButton } from './QuickViewButton';
import { AddToCartButton } from './AddToCartButton';
import { ProductTitle } from './ProductTitle';
import { ProductPicture } from './ProductPicture';
import ProductShippingEstimate from './ProductShippingEstimate';
import ProductFitment from './ProductFitment';
import { useQuery } from '@tanstack/react-query';
import { getPublicUserDoc } from '@util/firestore/users';
import TopRatedSellerBadge from './TopRatedSellerBadge';

const MXProductCard = ({
  product,
  list_id,
  list_name,
  attribution,
  bgColor,
  priority,
}: {
  product: ProductDocument;
  seller?: PublicUserDocument;
  list_id?: string;
  list_name?: string;
  attribution: OrderItemDocument['attribution'];
  bgColor?: string;
  priority?: boolean;
}) => {
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const { increaseCartQty, canAddProductToCart, setCartOpen } =
    useShoppingCart();
  const { setAttribution, getAttribution } = useAttributionContext();
  const pathname = usePathname();
  const { userDoc, observeProduct } = useAuth();
  const { filters } = useSearch();
  const router = useRouter();

  const { data: sellerDoc } = useQuery({
    queryKey: ['seller_doc', product.seller_id],
    queryFn: () => getPublicUserDoc({ uid: product.seller_id }),
    enabled: Boolean(product.seller_id),
  });

  const handleAddToCart = () => {
    if (userDoc) {
      if (canAddProductToCart(product.id)) {
        const attribution = getAttribution(product.id);
        const cartItem = getCartItemFromProductDocument(
          product,
          null,
          attribution
        );

        increaseCartQty(cartItem);
        setCartOpen(true);
      } else {
        setCartOpen(true);
      }
    } else {
      router.push(`/login?redirect=${encodeURI(pathname ?? '')}`);
    }
  };

  const quickViewButtonControls = useAnimation();

  const ref = useLogWhenInView(() => {
    if (userDoc?.uid) {
      const attribution = ['gear_for_you', 'parts_for_you'].includes(
        list_id ?? ''
      )
        ? {
            page: 'for_you',
            section: list_id ?? '',
            data_source: 'typesense',
          }
        : getAttribution(product.id) ?? '';
      observeProduct({
        id: product.id,
        attribution: attribution ?? '',
      });
    }
  });

  return (
    <MotionDiv
      ref={ref}
      className={cn(
        'group relative mx-auto flex h-fit w-[22rem] flex-col rounded-brand sm:mx-0 lg:w-[29rem]',
        bgColor
      )}
      onHoverStart={() => {
        quickViewButtonControls.start({
          opacity: 1,
          transition: { duration: 0.2, ease: 'linear' },
        });
      }}
      onHoverEnd={() => {
        quickViewButtonControls.start({
          opacity: 0,
          transition: { duration: 0.2, ease: 'linear' },
          x: 0,
        });
      }}
    >
      <Link
        href={getProductSlug(product)}
        className="flex h-full flex-col"
        onClick={() => {
          setAttribution(product.id, attribution);
          logEvent('select_item', {
            items: [getItem(product)],
            item_list_id: list_id ?? pathname,
            item_list_name: list_name ?? pathname,
          });
          if (pathname?.includes('search') || pathname?.includes('product')) {
            if (userDoc)
              trackUserInteraction({
                uid: userDoc.uid,
                pid: product.id,
                interaction: 'search_clicked',
              });
            if (filters?.term) {
              trackUserSearch({
                ...filters,
                product_clicked: product.id,
              });
            }
          }
        }}
      >
        <div className="flex h-full flex-col">
          <ProductPicture product={product} priority={priority} />

          <div className="space-y- mt-2 flex flex-grow flex-col justify-between p-[0.8rem]">
            <div className="space-y-2">
              {sellerDoc?.top_seller && <TopRatedSellerBadge />}

              <ProductTitle product={product} />

              <ProductPriceRange
                product={product}
                vertical
                enableCouponDiscount
              />

              <ProductShippingEstimate userDoc={userDoc} product={product} />

              {userDoc && !!userDoc.bikes.length && (
                <ProductFitment product={product} userDoc={userDoc} />
              )}

              {product.sold_count && product.sold_count >= 5 && (
                <span className="block text-[1.6rem] font-medium text-brand-secondary sm:text-[1.7rem]">
                  {product.sold_count} sold
                </span>
              )}

              {/* {!!product.stock &&
                product.stock <= 3 &&
                !product.has_variations && (
                  <span className="block text-[1.6rem] font-semibold text-brand-secondary sm:text-[1.7rem]">
                    {product.stock === 1
                      ? 'Only one left'
                      : `Only ${product.stock} left`}
                  </span>
                )} */}
            </div>
          </div>
        </div>
      </Link>

      <div className="absolute right-[1rem] top-[1rem] flex w-full justify-end sm:right-[1rem]">
        <FavoriteButton
          product={product}
          initialCount={product.favorite_count}
        />
      </div>

      <QuickViewModal
        product={product}
        isOpen={quickViewOpen}
        dismiss={() => setQuickViewOpen(false)}
      />

      {pathname !== '/my-cart' ? (
        product.category !== 'Dirt Bikes' &&
        !product.is_auction &&
        !(product.variations && product.variations.length > 0) && (
          <div className="absolute top-[23rem] hidden w-full justify-center sm:flex">
            <QuickViewButton
              product={product}
              setQuickViewOpen={setQuickViewOpen}
              attribution={attribution}
              setAttribution={setAttribution}
              quickViewButtonControls={quickViewButtonControls}
            />
          </div>
        )
      ) : (
        <div className="absolute right-[1rem] top-[15rem] flex w-full cursor-pointer items-end justify-end lg:right-[2rem] lg:top-[23rem]">
          <AddToCartButton handleAddToCart={handleAddToCart} />
        </div>
      )}
    </MotionDiv>
  );
};

export default MXProductCard;
